import React, { useEffect, useState, useMemo } from "react";
import { checkEnabled } from "../../../utils/libraryUtils";
import { getGroupReviewerContent } from "../../../services/api";

const CreateChallengeModal = ({
  isOpen,
  onClose,
  groupId,
  sharedReviewers = [],
  initialReviewerId = null,
  onCreateChallenge,
}) => {
  // Filter for shared reviewers that have quiz mode enabled
  const eligibleReviewers = useMemo(() => {
    return sharedReviewers.filter((msg) => {
      const config = msg?.reviewer?.config || {};
      return checkEnabled(config.quizEnabled) && msg?.reviewer?._id;
    });
  }, [sharedReviewers]);

  const [selectedReviewerId, setSelectedReviewerId] = useState("");
  const [title, setTitle] = useState("");
  const [timePerQuestion, setTimePerQuestion] = useState(20);
  const [questionCount, setQuestionCount] = useState(10);
  const [availableQuestionsCount, setAvailableQuestionsCount] = useState(10);
  const [loadingQuestionsCount, setLoadingQuestionsCount] = useState(false);
  const [durationHours, setDurationHours] = useState(24);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    setErrorMsg("");
    let target = "";
    if (initialReviewerId && eligibleReviewers.some((r) => String(r.reviewer?._id) === String(initialReviewerId))) {
      target = initialReviewerId;
    } else if (eligibleReviewers.length > 0) {
      target = eligibleReviewers[0].reviewer._id;
    }

    setSelectedReviewerId(target);
    const revMsg = eligibleReviewers.find((r) => String(r.reviewer?._id) === String(target));
    const revTitle = revMsg?.reviewer?.topic || revMsg?.reviewer?.title || "Module";
    setTitle(`${revTitle} Sprint Challenge`);
  }, [isOpen, initialReviewerId, eligibleReviewers]);

  // Fetch actual question count for the selected reviewer
  useEffect(() => {
    if (!isOpen || !selectedReviewerId) return;

    let isMounted = true;
    const revMsg = eligibleReviewers.find((r) => String(r.reviewer?._id) === String(selectedReviewerId));
    const fallbackCount = Number(revMsg?.reviewer?.config?.questionCount) || 10;
    setAvailableQuestionsCount(fallbackCount);

    if (groupId) {
      setLoadingQuestionsCount(true);
      getGroupReviewerContent(groupId, selectedReviewerId)
        .then((res) => {
          if (!isMounted) return;
          const data = res?.data || {};
          const list =
            data.quiz ||
            data.questions ||
            data.quizQuestions ||
            (Array.isArray(data) ? data : []);
          if (Array.isArray(list) && list.length > 0) {
            setAvailableQuestionsCount(list.length);
            setQuestionCount((prev) => Math.min(prev, list.length));
          }
        })
        .catch(() => {})
        .finally(() => {
          if (isMounted) setLoadingQuestionsCount(false);
        });
    }

    return () => {
      isMounted = false;
    };
  }, [isOpen, selectedReviewerId, groupId, eligibleReviewers]);

  const handleReviewerChange = (revId) => {
    setSelectedReviewerId(revId);
    const revMsg = eligibleReviewers.find((r) => String(r.reviewer?._id) === String(revId));
    const revTitle = revMsg?.reviewer?.topic || revMsg?.reviewer?.title || "Module";
    setTitle(`${revTitle} Sprint Challenge`);
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedReviewerId) {
      setErrorMsg("Please select a reviewer for this challenge.");
      return;
    }
    if (!title.trim()) {
      setErrorMsg("Please provide a title for the challenge.");
      return;
    }

    try {
      setSubmitting(true);
      setErrorMsg("");
      const safeCount = Math.min(Number(questionCount), availableQuestionsCount || 10);
      await onCreateChallenge({
        reviewerId: selectedReviewerId,
        title: title.trim(),
        timePerQuestion: Number(timePerQuestion),
        questionCount: safeCount,
        totalAvailableQuestions: availableQuestionsCount || safeCount,
        durationHours: Number(durationHours),
      });
      onClose();
    } catch (err) {
      setErrorMsg(err.response?.data?.message || err.message || "Failed to launch challenge.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden border border-gray-100 animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 bg-gradient-to-r from-violet-600 via-indigo-600 to-blue-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/15 backdrop-blur-md flex items-center justify-center text-amber-300 text-lg shadow-inner border border-white/20">
              <i className="fa-solid fa-bolt"></i>
            </div>
            <div>
              <h2 className="text-lg font-black tracking-tight leading-tight">Launch Group Challenge</h2>
              <p className="text-xs text-indigo-100 mt-0.5">Test group knowledge under the clock</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white transition flex items-center justify-center"
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark text-sm"></i>
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {errorMsg && (
            <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold flex items-center gap-2">
              <i className="fa-solid fa-triangle-exclamation text-rose-500 text-sm"></i>
              <span>{errorMsg}</span>
            </div>
          )}

          {eligibleReviewers.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto mb-3 text-xl">
                <i className="fa-solid fa-file-circle-question"></i>
              </div>
              <h3 className="font-bold text-gray-800 text-sm">No Reviewers with Quizzes Found</h3>
              <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto">
                Share a reviewer into this study group with Quiz Mode enabled to start a challenge.
              </p>
            </div>
          ) : (
            <>
              {/* Reviewer Select */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  1. Select Module / Reviewer
                </label>
                <select
                  value={selectedReviewerId}
                  onChange={(e) => handleReviewerChange(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50/50"
                  required
                >
                  {eligibleReviewers.map((msg) => (
                    <option key={msg.reviewer?._id} value={msg.reviewer?._id}>
                      {msg.reviewer?.topic || msg.reviewer?.title || "Reviewer"}
                    </option>
                  ))}
                </select>
              </div>

              {/* Title Input */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  2. Challenge Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={80}
                  placeholder="e.g. Midterm Sprint Showdown"
                  className="w-full px-3.5 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              {/* Time Per Question */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  3. Time Limit per Question
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {[15, 20, 30, 45, 60].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTimePerQuestion(t)}
                      className={`px-3 py-2 rounded-xl text-xs font-bold border transition text-center ${
                        timePerQuestion === t
                          ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                          : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      {t}s
                    </button>
                  ))}
                </div>
              </div>

              {/* Question Count */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                    4. Question Count
                  </label>
                  <span className="text-[11px] font-semibold text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                    {loadingQuestionsCount ? (
                      <>
                        <i className="fa-solid fa-circle-notch fa-spin text-[10px]"></i>
                        <span>Checking module questions...</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-list-check text-[10px]"></i>
                        <span>{availableQuestionsCount} Qs in module</span>
                      </>
                    )}
                  </span>
                </div>

                <div className="grid grid-cols-4 gap-2">
                  {[5, 10, 15, 20].map((q) => {
                    const isExceeding = !loadingQuestionsCount && q > availableQuestionsCount;
                    const isSelected = questionCount === q;
                    return (
                      <button
                        key={q}
                        type="button"
                        disabled={isExceeding}
                        onClick={() => setQuestionCount(q)}
                        className={`px-3 py-2 rounded-xl text-xs font-bold border transition text-center flex flex-col items-center justify-center gap-0.5 ${
                          isExceeding
                            ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50"
                            : isSelected
                            ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                            : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                        }`}
                        title={
                          isExceeding
                            ? `Only ${availableQuestionsCount} questions exist in this reviewer`
                            : `${q} questions`
                        }
                      >
                        <span>{q} Qs</span>
                        {isExceeding && (
                          <span className="text-[9px] font-normal leading-none">(Max {availableQuestionsCount})</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {availableQuestionsCount < 20 && !loadingQuestionsCount && (
                  <p className="text-[11px] text-gray-400 mt-1">
                    * This reviewer was generated with {availableQuestionsCount} questions in total.
                  </p>
                )}
              </div>

              {/* Duration Window */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                  5. Challenge Duration (Time Window)
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {[
                    { val: 2, label: "2 hrs" },
                    { val: 6, label: "6 hrs" },
                    { val: 12, label: "12 hrs" },
                    { val: 24, label: "24 hrs" },
                    { val: 72, label: "3 days" },
                  ].map((d) => (
                    <button
                      key={d.val}
                      type="button"
                      onClick={() => setDurationHours(d.val)}
                      className={`px-2 py-2 rounded-xl text-xs font-bold border transition text-center ${
                        durationHours === d.val
                          ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                          : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Footer Buttons */}
          <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2.5 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-100 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || eligibleReviewers.length === 0}
              className="px-5 py-2.5 rounded-xl text-xs font-black bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-md shadow-indigo-600/20 transition active:scale-95 disabled:opacity-50 flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <i className="fa-solid fa-circle-notch fa-spin"></i>
                  <span>Launching...</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-bolt text-amber-300"></i>
                  <span>Start Challenge</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateChallengeModal;
